import React, { useRef, useState } from 'react';
import './ManualFileUploadForm.scss';
import ButtonLoader from '../../../../../../components/Layout/Buttons/ButtonLoader';

import { Feedback, Input, Field } from '@availity/form';
import { Formik } from 'formik';
import { Col, Label, Row } from 'reactstrap';
import { FileSelector } from '../../../../../../components/FileSelector/FileSelector';
import {
    ActionDocumentType,
    EngagementDocumentTypeOptionsMapping,
} from '../../../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { EngagementDocumentSource } from '../../../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { TimelineParty } from '../../../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import * as yup from 'yup';
import { MarketplaceDocumentSectionField } from '../../../../../AdminMarketplace/ActionDefinitionForm/DocumentSectionField/MarketplaceDocumentSectionField';
import { useStateSelector } from '../../../../../../store/selectors';
import {
    createDefaultDocumentDetails,
    DocumentDetails,
} from '../../../../../../slices/marketplace/timeline/models/document-details.model';
import { DocumentSection } from '../../../../../../slices/marketplace/timeline/models/document-section.model';
import ReactSwitch from 'react-switch';
import {
    DocumentPartyVisibility,
    DocumentPartyVisibilityOptionsMapping,
} from '../../../../../../slices/marketplace/timeline/models/enums/engagement-document-party-visibility';
import {
    DataNdaSignedConditionsOptionsMapping,
    DocumentBuyerVisibilityConditions,
} from '../../../../../../slices/marketplace/timeline/models/enums/engagement-document-party-buyer-visibility-conditions';
import { FileSelectorService } from '../../../../../../components/FileSelector/FileSelector.service';
import { DocumentDetailsStatus } from '../../../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { useCurrentMarketplaceParty } from '../../../../../../custom-hooks/useCurrentMarketplaceParty';

interface ManualFileUploadFormProps {
    isVisible: boolean;
    onClose: () => void;
    onSubmit(document: DocumentDetails): void;
    documentsSections: DocumentSection[];
    onSectionsButtonClick: () => void;
    isSaving: boolean;
    document: DocumentDetails;
    deleteDocument(documentId: string): void;
    setIsDocumentsHistoryPopupVisible(isVisible: boolean): void;
    downloadFile(): void;
}

export const ManualFileUploadForm = (props: ManualFileUploadFormProps) => {
    const formRef = useRef<any>();
    const axios = useStateSelector((state) => state.core.axios);
    const party = useCurrentMarketplaceParty();
    const isBroker = party === TimelineParty.Broker;
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isFileUpdated, setIsFileUpdated] = useState(false);
    const [isIdNdaConditionSelected, setIsIdNdaConditionSelected] = useState(
        props.document?.buyerVisibilityConditions?.includes(
            DocumentBuyerVisibilityConditions.IdNdaSigned
        )
    );
    const [
        isDataNdaSignedConditionSelected,
        setIsDataNdaSignedConditionSelected,
    ] = useState(
        props.document?.buyerVisibilityConditions?.some(
            (c) =>
                c === DocumentBuyerVisibilityConditions.DataNdaSignedAny ||
                c ===
                    DocumentBuyerVisibilityConditions.DataNdaSignedFullyBlinded ||
                c ===
                    DocumentBuyerVisibilityConditions.DataNdaSignedDvmsBlinded ||
                c ===
                    DocumentBuyerVisibilityConditions.DataNdaSignedStaffBlinded ||
                c ===
                    DocumentBuyerVisibilityConditions.DataNdaSignedFullyVisible
        ) ?? false
    );
    const [isPaidDfmConditionSelected, setIsPaidDfmConditionSelected] =
        useState(
            props.document?.buyerVisibilityConditions?.includes(
                DocumentBuyerVisibilityConditions.DfmFeePaid
            )
        );
    const [
        isFileApprovedConditionSelected,
        setIsFileApprovedConditionSelected,
    ] = useState(
        props.document?.buyerVisibilityConditions?.includes(
            DocumentBuyerVisibilityConditions.FileApprovedBySeller
        )
    );

    const getDocDataNdaTermCondition = () => {
        return (
            props.document?.buyerVisibilityConditions?.find(
                (c) =>
                    c === DocumentBuyerVisibilityConditions.DataNdaSignedAny ||
                    c ===
                        DocumentBuyerVisibilityConditions.DataNdaSignedFullyBlinded ||
                    c ===
                        DocumentBuyerVisibilityConditions.DataNdaSignedDvmsBlinded ||
                    c ===
                        DocumentBuyerVisibilityConditions.DataNdaSignedStaffBlinded ||
                    c ===
                        DocumentBuyerVisibilityConditions.DataNdaSignedFullyVisible
            ) ?? -1
        );
    };

    const [selectedTerms, setSelectedTerms] = useState<number>(
        getDocDataNdaTermCondition()
    );

    const [documentDetails, setDocumentDetails] = useState<DocumentDetails>(
        props.document ?? {
            ...createDefaultDocumentDetails(
                true,
                null,
                ActionDocumentType.DocumentWithFeedback,
                null,
                EngagementDocumentSource.AdminUpload,
                TimelineParty.Seller,
                true
            ),
            status: DocumentDetailsStatus.RequiresAction,
        }
    );

    const [fileSelectorService] = useState(new FileSelectorService(axios));

    const uploadFile = async () => {
        const fileId = await fileSelectorService.uploadFile(selectedFile);
        return {
            ...documentDetails,
            temporaryFileId: fileId,
            fileName: selectedFile?.name,
        };
    };

    const changeFileHandler = (file: File) => {
        if (file) {
            setIsFileUpdated(true);
            setSelectedFile(file);
            formRef.current.setFieldValue('fileName', file.name, true);
            formRef.current.setFieldValue('sourceFile', file.name, true);

            const newDocument: DocumentDetails = {
                ...documentDetails,
                name: file.name,
                isUnregisteredFile: true,
                localFile: file,
            };

            setDocumentDetails(newDocument);
        }
    };

    const handleUploadDocument = (documentDetails?: DocumentDetails) => {
        if (documentDetails?.isUnregisteredFile) {
            documentDetails = { ...documentDetails, datePosted: new Date() };
        }
        props.onSubmit(documentDetails);
    };

    const handleDataNdaSwitch = (checked: boolean) => {
        setIsDataNdaSignedConditionSelected(checked);
        if (!checked) {
            setSelectedTerms(-1);
            formRef.current.setFieldValue('selectedTerms', -1);
        }
    };

    const handleSelectedTermsChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = Number(e.target.value);
        setSelectedTerms(value);
    };

    const getCurrentVisibilityConditions = () => {
        const conditions: DocumentBuyerVisibilityConditions[] = [];
        if (isIdNdaConditionSelected) {
            conditions.push(DocumentBuyerVisibilityConditions.IdNdaSigned);
        }
        if (isDataNdaSignedConditionSelected) {
            conditions.push(selectedTerms);
        }
        if (isPaidDfmConditionSelected) {
            conditions.push(DocumentBuyerVisibilityConditions.DfmFeePaid);
        }
        if (isFileApprovedConditionSelected) {
            conditions.push(
                DocumentBuyerVisibilityConditions.FileApprovedBySeller
            );
        }
        return conditions;
    };

    return (
        <div className="manual-file-upload-form">
            <Formik
                innerRef={formRef}
                initialValues={{
                    documentSection: props.document?.sectionId || '-1',
                    documentName: props.document?.name || '',
                    sourceFile: props.document?.fileName || '',
                    partyVisibility: props.document?.partyVisibility,
                    selectedTerms: getDocDataNdaTermCondition(),
                }}
                validationSchema={yup.object().shape({
                    documentSection: yup
                        .string()
                        .required('Document section is required')
                        .notOneOf(['-1'], 'Document section is required'),
                    documentName: yup
                        .string()
                        .trim()
                        .required('Document name is required'),
                    partyVisibility: yup
                        .string()
                        .required('Party visibility is required')
                        .notOneOf(['-1'], 'Party visibility is required'),
                    sourceFile: yup
                        .string()
                        .required('Source File is required'),
                    selectedTerms: yup.number().when([], {
                        is: () => isDataNdaSignedConditionSelected,
                        then: (schema) =>
                            schema
                                .notOneOf([-1], 'Selected Terms is required')
                                .required('Selected Terms is required'),
                        otherwise: (schema) => schema,
                    }),
                })}
                onSubmit={async (values) => {
                    if (
                        props.documentsSections.every(
                            (d) => d.id !== values.documentSection
                        )
                    ) {
                        formRef.current.setFieldValue('documentSection', '-1');
                        formRef.current.validateForm();
                        return;
                    }
                    let docDetails = isFileUpdated
                        ? await uploadFile()
                        : documentDetails;

                    const partyVisibility = values.partyVisibility
                        ? Number(values.partyVisibility)
                        : -1;

                    const counterParty : TimelineParty =
                        partyVisibility ==
                        DocumentPartyVisibility.SellerAndBuyer
                            ? TimelineParty.SellerAndBuyer
                            : partyVisibility ==
                              DocumentPartyVisibility.AdminOnly
                            ? TimelineParty.VetValue
                            : TimelineParty.Seller;

                    docDetails = {
                        ...docDetails,
                        name: values.documentName.trim(),
                        sectionId: values.documentSection,
                        counterParty: counterParty,
                        partyVisibility: partyVisibility,
                        buyerVisibilityConditions:
                            getCurrentVisibilityConditions(),
                        source: isBroker
                            ? EngagementDocumentSource.BrokerUpload
                            : EngagementDocumentSource.AdminUpload,
                    };
                    handleUploadDocument(docDetails);
                }}>
                {({ values, setFieldValue }) => {
                    return (
                        <div>
                            <MarketplaceDocumentSectionField
                                documentsSections={props.documentsSections}
                                disabled={false}
                                setDocumentSectionList={() => {}}
                                documentDetailsList={[]}
                                type="sellers"
                                sectionsModalMode={'external-modal'}
                                onSectionsButtonClick={
                                    props.onSectionsButtonClick
                                }
                            />
                            <Field
                                className="field"
                                name="documentName"
                                label="Document Name"
                            />
                            <FileSelector
                                extension={
                                    EngagementDocumentTypeOptionsMapping.find(
                                        (o) =>
                                            Number(
                                                ActionDocumentType.DocumentWithFeedback
                                            ) === o.value
                                    ).fileExtensions
                                }
                                disabled={
                                    props.document?.type ===
                                        ActionDocumentType.DocumentWithFeedback &&
                                    props.document?.status ===
                                        DocumentDetailsStatus.Completed
                                }
                                file={selectedFile}
                                setFile={(file: File) => {
                                    changeFileHandler(file);
                                }}
                                fileName={values.sourceFile}
                                downloadFile={props.downloadFile}
                                setIsDocumentsHistoryPopupVisible={(
                                    isVisible
                                ) => {
                                    props.setIsDocumentsHistoryPopupVisible(
                                        isVisible
                                    );
                                }}
                                oldDocumentDetailsList={
                                    documentDetails.oldManualReferenceIds ?? []
                                }
                            />
                            <Feedback
                                name="sourceFile"
                                className="source-file-feedback"
                            />
                            <Label for="partyVisibility">
                                Party Visibility
                            </Label>
                            <Input
                                type="select"
                                name="partyVisibility"
                                onChange={() => {
                                    setIsIdNdaConditionSelected(false);
                                    setIsDataNdaSignedConditionSelected(false);
                                    setIsPaidDfmConditionSelected(false);
                                    setIsFileApprovedConditionSelected(false);
                                    setSelectedTerms(-1);
                                    setFieldValue('selectedTerms', -1);
                                }}>
                                <option value={-1}>Select</option>
                                {DocumentPartyVisibilityOptionsMapping.map(
                                    (option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}>
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </Input>
                            <Feedback name="partyVisibility" />
                            {Number(values.partyVisibility) ===
                            DocumentPartyVisibility.SellerAndBuyer ? (
                                <div className="buyer-visibility-conditions">
                                    <div className="buyer-visibility-conditions-header">
                                        Buyer Visibility Conditions:
                                    </div>
                                    <Col>
                                        <div className="field-switcher">
                                            <ReactSwitch
                                                offColor="#d92550"
                                                checked={
                                                    isIdNdaConditionSelected
                                                }
                                                onChange={(checked) =>
                                                    setIsIdNdaConditionSelected(
                                                        checked
                                                    )
                                                }
                                                height={22}></ReactSwitch>
                                            <div className="field-label">
                                                Id NDA Signed
                                            </div>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="field-switcher">
                                            <ReactSwitch
                                                offColor="#d92550"
                                                checked={
                                                    isDataNdaSignedConditionSelected
                                                }
                                                onChange={handleDataNdaSwitch}
                                                height={22}></ReactSwitch>
                                            <div className="field-label">
                                                Data NDA Signed
                                            </div>
                                        </div>
                                    </Col>
                                    {isDataNdaSignedConditionSelected ? (
                                        <Col>
                                            <Label for="partyVisibility">
                                                Selected Terms
                                            </Label>
                                            <Input
                                                type="select"
                                                name="selectedTerms"
                                                onChange={
                                                    handleSelectedTermsChange
                                                }>
                                                <option value={-1}>
                                                    Select
                                                </option>
                                                {DataNdaSignedConditionsOptionsMapping.map(
                                                    (option) => (
                                                        <option
                                                            key={option.value}
                                                            value={
                                                                option.value
                                                            }>
                                                            {option.label}
                                                        </option>
                                                    )
                                                )}
                                            </Input>
                                            <Feedback name="selectedTerms" />
                                        </Col>
                                    ) : (
                                        <></>
                                    )}
                                    <Col>
                                        <div className="field-switcher">
                                            <ReactSwitch
                                                offColor="#d92550"
                                                checked={
                                                    isPaidDfmConditionSelected
                                                }
                                                onChange={(checked) =>
                                                    setIsPaidDfmConditionSelected(
                                                        checked
                                                    )
                                                }
                                                height={22}></ReactSwitch>
                                            <div className="field-label">
                                                Paid for DFM Access
                                            </div>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="field-switcher">
                                            <ReactSwitch
                                                offColor="#d92550"
                                                checked={
                                                    isFileApprovedConditionSelected
                                                }
                                                onChange={(checked) =>
                                                    setIsFileApprovedConditionSelected(
                                                        checked
                                                    )
                                                }
                                                height={22}></ReactSwitch>
                                            <div className="field-label">
                                                File Approved by Seller
                                            </div>
                                        </div>
                                    </Col>
                                </div>
                            ) : (
                                []
                            )}
                        </div>
                    );
                }}
            </Formik>
            <div className="mt-auto d-flex justify-content-end buttons-container">
                <button
                    className="btn btn-outline-secondary mr-2"
                    onClick={() => {
                        props.onClose();
                    }}>
                    Cancel
                </button>
                {props.document ? (
                    <button
                        className="btn btn-primary mr-2"
                        onClick={() => {
                            props.deleteDocument(props.document.id);
                        }}>
                        Delete
                    </button>
                ) : (
                    []
                )}
                <ButtonLoader
                    type="submit"
                    buttonText={'Upload'}
                    loaderButtonText={''}
                    isLoading={props.isSaving}
                    disabled={false}
                    onClick={() => {
                        formRef?.current?.handleSubmit();
                    }}
                    className={'btn btn-primary'}
                />
            </div>
        </div>
    );
};
